import { useContext } from 'react';
import AppContext from '../../AppContext';
import {
  BookOfBusinessHeader,
  BookOfBusinessRow,
  BookOfBusinessRowData
} from '../RowBuilder/BookOfBusinessRow';
import {
  DirectLossesHeader,
  DirectLossesRow,
  DirectLossesRowData
} from '../RowBuilder/DirectLossesRow';
import {
  MedicalLossRatioRebatesHeader,
  MedicalLossRatioRebatesRow,
  MedicalLossRatioRebatesRowData
} from '../RowBuilder/MedicalLossRatioRebatesRow';
import {
  CurrentYearExpensesHeader,
  CurrentYearExpensesRow,
  CurrentYearExpensesRowData
} from '../RowBuilder/CurrentYearExpensesRow';
import {
  ReinsurersHeader,
  PropertyReinsurersRow,
  PropertyReinsurersRowData,
  LifeReinsurersRow,
  LifeReinsurersRowData
} from '../RowBuilder/ReinsurersRow';
import { LineOfBusinessHeader, LineOfBusinessRow } from '../RowBuilder/LineOfBusinessRow';
import { ReservesHeader, ReservesRow, ReservesRowData } from '../RowBuilder/ReservesRow';
import RowBuilder from '../RowBuilder/RowBuilder';
import { SubSectionType } from '../Section/SubSectionType';
import { SubSectionHeaderType } from '../Section/SubSectionHeaderType';
import { IrisRowHeader, StandardRowHeader } from '../SubSectionHeader';
import { HeaderHelmetProps } from '../SubSectionHeader/HeaderHelmet';
import { SubSectionData } from './SubSectionData';
import styles from './SubSection.module.css';
import ResultRowData from '../RowBuilder/ResultRowData';
import SubSectionInformation from './SubSectionInformation';
import {
  MedicalLossRatioSCHEHeader,
  MedicalLossRatioSCHERow,
  MedicalLossRatioSCHERowData
} from '../RowBuilder/MedicalLossRatioSCHERow';
import OperatingRatiosByLOBRowData from '../RowBuilder/OperatingRatiosByLOBRow/OperatingRatiosByLOBRowData';
import { OperatingRatiosByLOBRow } from '../RowBuilder/OperatingRatiosByLOBRow';
import OperatingRatiosByLOBHeader from '../RowBuilder/OperatingRatiosByLOBRow/OperatingRatiosByLOBHeader';
import {
  ComparisonOfPoliciesHeader,
  ComparisonOfPoliciesRow,
  ComparisonOfPoliciesRowData
} from '../RowBuilder/ComparisonOfPoliciesRow';
import { RollingRowHeader, RollingRow } from '../RowBuilder/RollingRow';
import { ComparableRowHeader } from '../RowBuilder/ComparableRow';
import PreviewHighlight from '../RowBuilder/PreviewHighlight';
import {
  QtrBookOfBusinessRowHeader,
  QtrBookOfBusinessRow
} from '../RowBuilder/QtrBookOfBusinessRow';
import QtrBookOfBusinessRowData from '../RowBuilder/QtrBookOfBusinessRow/QtrBookOfBusinessRowData';
import {
  QtrDirectPremiumRowHeader,
  QtrDirectPremiumRow
} from '../RowBuilder/QtrDirectPremiumRow';
import {
  QtrReinsurersRowHeader,
  QtrReinsurersRow
} from '../RowBuilder/QtrReinsurersRow';
interface SubSectionProps {
  subSectionData: SubSectionData;
  sectionKey: string;
}

export default function SubSection({ subSectionData, sectionKey }: SubSectionProps) {
  const { riskRepository, periodType, quarterlyViewMode } = useContext(AppContext);

  function getQuarterFromPeriodType(pt: string | null): string {
    if (!pt) return 'Q3';
    const match = pt.match(/QUARTER_(\d)/);
    return match ? `Q${match[1]}` : 'Q3';
  }
  function getHeader(subSectionType: SubSectionType = SubSectionType.ANNUAL_CHANGE) {
    let helmet: HeaderHelmetProps = {
      label: subSectionData?.subSectionTitle,
      hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis,
      subSectionHeaderType: subSectionData?.subSectionHeaderType
    };

    if (subSectionType === SubSectionType.IRIS) {
      return (
        <IrisRowHeader
          helmet={helmet}
          showDollarChange={subSectionData?.showDollarChange}
          showPercentPointChange={subSectionData?.showPercentPointChange}
        />
      );
    } else if (subSectionType === SubSectionType.RESERVES) {
      return (
        <ReservesHeader
          helmet={helmet}
          showDollarChange={subSectionData?.showDollarChange}
          showPercentPointChange={subSectionData?.showPercentPointChange}
        />
      );
    } else if (subSectionType === SubSectionType.BOOK_OF_BUSINESS) {
      return (
        <BookOfBusinessHeader
          helmet={helmet}
          showDollarChange={subSectionData?.showDollarChange}
          showPercentPointChange={subSectionData?.showPercentPointChange}
          includePDLR={subSectionData?.includePDLR}
          showIncludePy2={subSectionData?.showIncludePy2}
          sectionKey={sectionKey}
        />
      );
    } else if (subSectionType === SubSectionType.REINSURERS) {
      return <ReinsurersHeader helmet={helmet} />;
    } else if (subSectionType === SubSectionType.DIRECT_LOSSES) {
      return (
        <DirectLossesHeader
          helmet={helmet}
          showDollarChange={subSectionData?.showDollarChange}
          showPercentPointChange={subSectionData?.showPercentPointChange}
        />
      );
    } else if (subSectionType === SubSectionType.MLR_REBATES) {
      return <MedicalLossRatioRebatesHeader helmet={helmet} />;
    } else if (subSectionType === SubSectionType.CURRENT_YEAR_EXPENSES) {
      return <CurrentYearExpensesHeader helmet={helmet} />;
    } else if (subSectionType === SubSectionType.MLR_SHCE) {
      return (
        <MedicalLossRatioSCHEHeader
          helmet={helmet}
          showPercentPointChange={subSectionData?.showPercentPointChange}
        />
      );
    } else if (subSectionType === SubSectionType.OR_BY_LOB) {
      return <OperatingRatiosByLOBHeader helmet={helmet} />;
    } else if (subSectionType === SubSectionType.POLICY_COMPARISON) {
      return <ComparisonOfPoliciesHeader helmet={helmet} />;
    } else {
      return (
        <StandardRowHeader
          helmet={helmet}
          showDollarChange={subSectionData?.showDollarChange}
          showPercentPointChange={subSectionData?.showPercentPointChange}
          showTDPHeader={subSectionData?.showTDP}
          showPHSHeader={subSectionData?.showPHS}
          showPriorYearPHSHeader={subSectionData?.showPriorYearPHS}
          showNAAHeader={subSectionData?.showNAA}
          showCASHeader={subSectionData?.showCAS}
          showPriorYearCASHeader={subSectionData?.showPriorYearCAS}
          showCalculatePercentTotalCurrYearHeader={
            subSectionData?.showCalculatePercentTotalCurrYear
          }
          showIndustryMeanHeader={subSectionData?.annualChangeRows?.some(
            (row) => row.industryMean?.value != null
          )}
        />
      );
    }
  }

  function resultMapContains(resultRows: ResultRowData[], key: string) {
    return resultRows && resultRows.some((rowData) => rowData.resultMap && rowData.resultMap[key]);
  }

  if (
    subSectionData &&
    (riskRepository === 'FR' || subSectionData?.reportViewList?.includes(riskRepository))
  ) {
    switch (subSectionData.subSectionType) {
      case SubSectionType.LINE_OF_BUSINESS:
        const resultRows: ResultRowData[] = subSectionData.resultRows || [];
        const showReturnOnRevenue: boolean = resultMapContains(
          resultRows,
          'currentYearReturnOnRevenue'
        );
        const showLossRatio: boolean = resultMapContains(resultRows, 'currentYearLossRatio');
        const showExpenseRatio: boolean = resultMapContains(resultRows, 'currentYearExpenseRatio');
        return (
          <>
            <div className={styles.body} id={subSectionData.subSectionKey}>
              <LineOfBusinessHeader
                helmet={{
                  label: subSectionData?.subSectionTitle,
                  hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis
                }}
                showReturnOnRevenue={showReturnOnRevenue}
                showLossRatio={showLossRatio}
                showExpenseRatio={showExpenseRatio}
              />
              {resultRows && [
                resultRows.map((rowData: ResultRowData) => (
                  <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                    <LineOfBusinessRow key={rowData.rowKey} {...rowData} sectionKey={sectionKey} />
                  </PreviewHighlight>
                ))
              ]}
            </div>
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
      case SubSectionType.QTR_ROLLING: {
        const helmet: HeaderHelmetProps = {
          label: subSectionData?.subSectionTitle,
          hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis,
          subSectionHeaderType: subSectionData?.subSectionHeaderType
        };
        const supportsComparable = subSectionData?.isComparable === true;
        const useComparableView =
          supportsComparable && quarterlyViewMode === 'QTR_COMPARABLE';
        const headerProps = {
          helmet,
          showPHS: subSectionData?.showPHS,
          showNAA: subSectionData?.showNAA,
          showCAS: subSectionData?.showCAS,
          showTDP: subSectionData?.showTDP,
          showCASPlusAVR: subSectionData?.showCASPlusAVR,
          showPYECAS: subSectionData?.showPYECAS,
          showPYEPHS: subSectionData?.showPYEPHS,
          quarter: getQuarterFromPeriodType(periodType),
          calculateChangePYQ: subSectionData?.calculateChangePYQ,
          calculateChangePYE: subSectionData?.calculateChangePYE,
          calculateChangePYTD: subSectionData?.calculateChangePYTD,
          calculateChange: subSectionData?.calculateChange,
          calculatePYIndustryAvg: subSectionData?.calculatePYIndustryAvg,
          include3Months: subSectionData?.include3Months,
          removeFromCalculateChangePYE: subSectionData?.removeFromCalculateChangePYE,
          removeFromCalculateChangePYQ: subSectionData?.removeFromCalculateChangePYQ,
          showPercentPointChange: subSectionData?.showPercentPointChange,
          showDollarChange: subSectionData?.showDollarChange
        };
        // Both rolling and comparable views render the same 5 quarterly fields
        // (currentQuarter, priorQuarter, priorQuarterMinusOne/Two/Three) from the
        // backend payload. Only the header labels differ. Use RollingRow for both
        // so the row bodies populate — RowBuilder reads currentYear/priorYear
        // fields which do not exist on quarterly payloads.
        const quarterlyRows =
          subSectionData?.rollingRows || subSectionData?.annualChangeRows;
        // Mirror the header's change-column gate: only render the %/Pt Change and $ Change
        // cells when a change flag is set, otherwise leave those cells blank (no '-').
        const showChangeColumns =
          !!subSectionData?.calculatePYIndustryAvg ||
          !!subSectionData?.calculateChangePYQ ||
          !!subSectionData?.calculateChangePYE ||
          !!subSectionData?.calculateChangePYTD ||
          !!subSectionData?.calculateChange;
        return (
          <>
            <div className={styles.body} id={subSectionData.subSectionKey}>
              {useComparableView ? (
                <ComparableRowHeader {...headerProps} />
              ) : (
                <RollingRowHeader {...headerProps} />
              )}
              {quarterlyRows?.map((rowData: any, i: number) => (
                <PreviewHighlight key={`ph-qr-${i}`} status={rowData.status}>
                  <RollingRow
                    key={`${rowData.rowKey || 'row'}-${i}`}
                    {...rowData}
                    sectionKey={sectionKey}
                    forceRollingPrior={!subSectionData?.isComparable}
                    calculatePYIndustryAvg={subSectionData?.calculatePYIndustryAvg}
                    quarter={getQuarterFromPeriodType(periodType)}
                    comparableColumns={useComparableView}
                    showChangeColumns={showChangeColumns}
                    showPercentPointChange={subSectionData?.showPercentPointChange}
                    showDollarChange={subSectionData?.showDollarChange}
                  />
                </PreviewHighlight>
              ))}
            </div>
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
      }
      case SubSectionType.QTR_COMPARABLE: {
        // QTR_COMPARABLE payload uses the same quarterly field names as QTR_ROLLING
        // (currentQuarter, priorQuarter, priorQuarterMinusOne/Two/Three). Use
        // RollingRow so those cells populate — RowBuilder reads currentYear/priorYear
        // which are undefined here.
        const helmet: HeaderHelmetProps = {
          label: subSectionData?.subSectionTitle,
          hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis,
          subSectionHeaderType: subSectionData?.subSectionHeaderType
        };
        const headerProps = {
          helmet,
          showPHS: subSectionData?.showPHS,
          showNAA: subSectionData?.showNAA,
          showCAS: subSectionData?.showCAS,
          showTDP: subSectionData?.showTDP,
          showCASPlusAVR: subSectionData?.showCASPlusAVR,
          showPYECAS: subSectionData?.showPYECAS,
          showPYEPHS: subSectionData?.showPYEPHS,
          quarter: getQuarterFromPeriodType(periodType),
          calculateChangePYQ: subSectionData?.calculateChangePYQ,
          calculateChangePYE: subSectionData?.calculateChangePYE,
          calculateChangePYTD: subSectionData?.calculateChangePYTD,
          calculateChange: subSectionData?.calculateChange,
          calculatePYIndustryAvg: subSectionData?.calculatePYIndustryAvg,
          removeFromCalculateChangePYE: subSectionData?.removeFromCalculateChangePYE,
          removeFromCalculateChangePYQ: subSectionData?.removeFromCalculateChangePYQ,
          showPercentPointChange: subSectionData?.showPercentPointChange,
          showDollarChange: subSectionData?.showDollarChange
        };
        // Mirror the header's change-column gate: only render the %/Pt Change and $ Change
        // cells when a change flag is set, otherwise leave those cells blank (no '-').
        const showChangeColumns =
          !!subSectionData?.calculatePYIndustryAvg ||
          !!subSectionData?.calculateChangePYQ ||
          !!subSectionData?.calculateChangePYE ||
          !!subSectionData?.calculateChangePYTD ||
          !!subSectionData?.calculateChange;
        return (
          <>
            <div className={styles.body} id={subSectionData.subSectionKey}>
              <ComparableRowHeader {...headerProps} />
              {subSectionData?.annualChangeRows?.map((rowData: any, i: number) => (
                <PreviewHighlight key={`ph-qc-${i}`} status={rowData.status}>
                  <RollingRow
                    key={`${rowData.rowKey || 'row'}-${i}`}
                    {...rowData}
                    sectionKey={sectionKey}
                    calculatePYIndustryAvg={subSectionData?.calculatePYIndustryAvg}
                    quarter={getQuarterFromPeriodType(periodType)}
                    comparableColumns={true}
                    showChangeColumns={showChangeColumns}
                    showPercentPointChange={subSectionData?.showPercentPointChange}
                    showDollarChange={subSectionData?.showDollarChange}
                  />
                </PreviewHighlight>
              ))}
            </div>
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
      }
      case SubSectionType.QTR_REINSURERS: {
        const helmet: HeaderHelmetProps = {
          label: subSectionData?.subSectionTitle,
          hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis,
          subSectionHeaderType: subSectionData?.subSectionHeaderType
        };
        return (
          <>
            <div className={styles.body} id={subSectionData.subSectionKey}>
              <QtrReinsurersRowHeader
                helmet={helmet}
                quarter={getQuarterFromPeriodType(periodType)}
              />
              {subSectionData?.annualChangeRows?.map((rowData, i) => (
                <PreviewHighlight key={`ph-qr-${i}`} status={(rowData as any).status}>
                  <QtrReinsurersRow
                    key={(rowData as any).rowKey || `qtr-reins-${i}`}
                    {...(rowData as any)}
                    sectionKey={sectionKey}
                    subSectionKey={subSectionData.subSectionKey}
                  />
                </PreviewHighlight>
              ))}
            </div>
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
      }
      case SubSectionType.QTR_BOOK_OF_BUSINESS: {
        const helmet: HeaderHelmetProps = {
          label: subSectionData?.subSectionTitle,
          hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis,
          subSectionHeaderType: subSectionData?.subSectionHeaderType
        };
        // The API sends the Loss Ratio layout via the subSectionHeaderType. The Line Of
        // Business layout ("LOSSES_INCURRED_AND_RATIO") shows a Loss Ratio column; the
        // States/Territories layout ("LOSSES_PAID") does not.
        const includeLossRatio =
          subSectionData?.includeLossRatio ??
          subSectionData?.subSectionHeaderType ===
            SubSectionHeaderType.LOSSES_INCURRED_AND_RATIO;
        const lossesLabel =
          subSectionData?.lossesLabel ?? (includeLossRatio ? 'Losses Incurred' : 'Losses Paid');
        // The backend returns Book of Business rows under `bookOfBusinessRows` using the
        // shared currentYear*/priorYear* field names. Map them onto the quarterly row shape.
        const qtrBookOfBusinessRows =
          subSectionData?.qtrBookOfBusinessRows ??
          (subSectionData?.bookOfBusinessRows as any[])?.map((row: any) => ({
            rowKey: row.rowKey,
            rowType: row.rowType,
            rowClass: row.rowClass,
            label: row.label,
            benchmark: row.benchmark,
            rowDisplay: row.rowDisplay,
            calculateDetail: row.calculateDetail,
            rowDataSource: row.rowDataSource,
            rowLinkConfiguration: row.rowLinkConfiguration,
            premiumPercentChange: row.percentOrPointChange,
            cytdPremium: row.currentYearPremium,
            cytdPercentOfTotal: row.currentYearPercentOfTotal,
            cytdLosses: row.currentYearLossPaid,
            cytdLossRatio: row.currentYearPDLR,
            pytdPremium: row.priorYearPremium,
            pytdPercentOfTotal: row.priorYearPercentOfTotal,
            pytdLosses: row.priorYearLossPaid,
            pytdLossRatio: row.priorYearPDLR
          }));
        return (
          <>
            <div className={styles.body} id={subSectionData.subSectionKey}>
              <QtrBookOfBusinessRowHeader
                helmet={helmet}
                includeLossRatio={includeLossRatio}
                lossesLabel={lossesLabel}
              />
              {qtrBookOfBusinessRows?.map((rowData: QtrBookOfBusinessRowData, i: number) => (
                <PreviewHighlight key={`ph-qbob-${i}`} status={(rowData as any).status}>
                  <QtrBookOfBusinessRow
                    key={rowData.rowKey || `qtr-bob-${i}`}
                    {...rowData}
                    sectionKey={sectionKey}
                    subSectionKey={subSectionData.subSectionKey}
                    includeLossRatio={includeLossRatio}
                  />
                </PreviewHighlight>
              ))}
            </div>
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
      }
      case SubSectionType.QTR_DIR_PREM: {
        const helmet: HeaderHelmetProps = {
          label: subSectionData?.subSectionTitle,
          hasBenchmarkAnalysis: subSectionData?.hasBenchmarkAnalysis,
          subSectionHeaderType: subSectionData?.subSectionHeaderType
        };
        return (
          <>
            <div className={styles.body} id={subSectionData.subSectionKey}>
              <QtrDirectPremiumRowHeader
                helmet={helmet}
                showPremiumPercentChange={subSectionData?.showPercentPointChange}
                showDollarNumberChange={subSectionData?.showDollarChange}
              />
              {subSectionData?.bookOfBusinessRows?.map((rowData: any, i: number) => (
                <PreviewHighlight key={`ph-qdp-${i}`} status={rowData.status}>
                  <QtrDirectPremiumRow
                    key={rowData.rowKey || `qtr-dp-${i}`}
                    rowKey={rowData.rowKey || `qtr-dp-${i}`}
                    rowType={rowData.rowType || 'AMOUNT'}
                    label={rowData.label || ''}
                    benchmark={rowData.benchmark || ''}
                    rowClass={rowData.rowClass}
                    rowDisplay={rowData.rowDisplay}
                    sectionKey={sectionKey}
                    subSectionKey={subSectionData.subSectionKey}
                    premiumPercentChange={rowData.percentOrPointChange}
                    premiumDollarChange={rowData.dollarChange}
                    cqDirectPremium={rowData.currentYearPremium}
                    cqPercentOfTotal={rowData.currentYearPercentOfTotal}
                    pyqDirectPremium={rowData.priorYearPremium}
                    pyqPercentOfTotal={rowData.priorYearPercentOfTotal}
                    showPremiumPercentChange={subSectionData?.showPercentPointChange}
                    showDollarNumberChange={subSectionData?.showDollarChange}
                  />
                </PreviewHighlight>
              ))}
            </div>
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
      }
      default:
        return (
          <>
            {subSectionData && (
              <div className={styles.body} id={subSectionData.subSectionKey}>
                {getHeader(subSectionData.subSectionType)}
                {subSectionData?.annualChangeRows && [
                  subSectionData.annualChangeRows.map((rowData, i) => (
                    <PreviewHighlight key={`ph-ac-${i}`} status={(rowData as any).status}>
                      <RowBuilder
                        data={rowData}
                        sectionKey={sectionKey}
                        showPercentPointChange={subSectionData?.showPercentPointChange}
                        showDollarChange={subSectionData?.showDollarChange}
                        key={i}
                      />
                    </PreviewHighlight>
                  ))
                ]}
                {subSectionData?.reservesRows && [
                  subSectionData?.reservesRows.map((rowData: ReservesRowData) => (
                    <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                      <ReservesRow key={rowData.rowKey} {...rowData} sectionKey={sectionKey} />
                    </PreviewHighlight>
                  ))
                ]}
                {subSectionData?.bookOfBusinessRows && [
                  subSectionData?.bookOfBusinessRows.map((rowData: BookOfBusinessRowData, index: number) => (
                    <PreviewHighlight key={`ph-${rowData.rowKey ?? index}`} status={(rowData as any).status}>
                      <BookOfBusinessRow
                        key={rowData.rowKey ?? index}
                        {...rowData}
                        sectionKey={sectionKey}
                        subSectionKey={subSectionData.subSectionKey}
                      />
                    </PreviewHighlight>
                  ))
                ]}
                {subSectionData?.propertyReinsurersRows && [
                  subSectionData?.propertyReinsurersRows.map(
                    (rowData: PropertyReinsurersRowData) => (
                      <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                        <PropertyReinsurersRow
                          key={rowData.rowKey}
                          {...rowData}
                          sectionKey={sectionKey}
                        />
                      </PreviewHighlight>
                    )
                  )
                ]}
                {subSectionData?.lifeReinsurersRows && [
                  subSectionData?.lifeReinsurersRows.map((rowData: LifeReinsurersRowData) => (
                    <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                      <LifeReinsurersRow key={rowData.rowKey} {...rowData} sectionKey={sectionKey} />
                    </PreviewHighlight>
                  ))
                ]}
                {subSectionData?.directLossesRows && [
                  subSectionData?.directLossesRows.map((rowData: DirectLossesRowData) => (
                    <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                      <DirectLossesRow key={rowData.rowKey} {...rowData} sectionKey={sectionKey} />
                    </PreviewHighlight>
                  ))
                ]}
                {subSectionData?.medicalLossRatioRebatesRows && [
                  subSectionData?.medicalLossRatioRebatesRows.map(
                    (rowData: MedicalLossRatioRebatesRowData) => (
                      <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                        <MedicalLossRatioRebatesRow
                          key={rowData.rowKey}
                          {...rowData}
                          sectionKey={sectionKey}
                        />
                      </PreviewHighlight>
                    )
                  )
                ]}
                {subSectionData?.currentYearExpensesRows && [
                  subSectionData?.currentYearExpensesRows.map(
                    (rowData: CurrentYearExpensesRowData) => (
                      <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                        <CurrentYearExpensesRow
                          key={rowData.rowKey}
                          {...rowData}
                          sectionKey={sectionKey}
                        />
                      </PreviewHighlight>
                    )
                  )
                ]}
                {subSectionData?.medicalLossRatioHealthCareRebatesRows && [
                  subSectionData?.medicalLossRatioHealthCareRebatesRows.map(
                    (rowData: MedicalLossRatioSCHERowData) => (
                      <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                        <MedicalLossRatioSCHERow
                          key={rowData.rowKey}
                          {...rowData}
                          sectionKey={sectionKey}
                        />
                      </PreviewHighlight>
                    )
                  )
                ]}
                {subSectionData?.comparisonOfPoliciesRows && [
                  subSectionData?.comparisonOfPoliciesRows.map(
                    (rowData: ComparisonOfPoliciesRowData) => (
                      <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                        <ComparisonOfPoliciesRow
                          key={rowData.rowKey}
                          {...rowData}
                          sectionKey={sectionKey}
                        />
                      </PreviewHighlight>
                    )
                  )
                ]}
                {subSectionData?.operatingRatiosByLOBRows && [
                  subSectionData?.operatingRatiosByLOBRows.map(
                    (rowData: OperatingRatiosByLOBRowData) => (
                      <PreviewHighlight key={`ph-${rowData.rowKey}`} status={(rowData as any).status}>
                        <OperatingRatiosByLOBRow
                          key={rowData.rowKey}
                          {...rowData}
                          sectionKey={sectionKey}
                        />
                      </PreviewHighlight>
                    )
                  )
                ]}
              </div>
            )}
            <SubSectionInformation
              subSectionKey={subSectionData.subSectionKey}
              informationList={subSectionData.subSectionHint}
            />
          </>
        );
    }
  }
}
